import { useTranslation } from 'i18n';
import { logger } from 'monitor';
import { Button, IconButton, Tooltip } from 'ui';
import { CheckCircleOutlined, MenuBook } from 'ui/icons';

import { CaptureButton } from '../CaptureButton/CaptureButton';
import { PreviewGallery } from '../PreviewGallery/PreviewGallery';
import { FooterControls, Root } from './styles';

export const CameraFooter = ({
  capture,
  captureDisabled,
  completeDisabled,
  slotProps,
  handleOpenGuide,
  handleOnComplete,
  isPortrait,
}: {
  capture: () => void;
  captureDisabled: boolean;
  completeDisabled: boolean;
  slotProps?: {
    previewGallery?: React.ComponentProps<typeof PreviewGallery>;
  };
  handleOpenGuide: () => void;
  handleOnComplete?: () => void;
  isPortrait: boolean;
}) => {
  const { t } = useTranslation('features');
  const completionTooltipMessage = completeDisabled
    ? t('cameraGuidance.cameraFooter.tooltips.takePhotoToComplete')
    : '';
  const captureAriaLabel = t('cameraGuidance.cameraFooter.actions.capture');
  const completeAriaLabel = t('cameraGuidance.cameraFooter.actions.complete');
  const guideAriaLabel = t('cameraGuidance.cameraFooter.actions.guide');
  const handleGuideClick = () => {
    logger.info('Camera footer guide button clicked', { isPortrait });
    handleOpenGuide();
  };
  const handleCaptureClick = () => {
    logger.info('Camera footer capture button clicked', {
      captureDisabled,
      isPortrait,
    });
    capture();
  };
  const handleCompleteClick = () => {
    logger.info('Camera footer complete button clicked', {
      completeDisabled,
      isPortrait,
    });
    handleOnComplete?.();
  };

  return (
    <Root isPortrait={isPortrait}>
      <PreviewGallery
        images={slotProps?.previewGallery?.images ?? []}
        onClick={slotProps?.previewGallery?.onClick}
        isPortrait={isPortrait}
      />
      <FooterControls isPortrait={isPortrait}>
        {isPortrait ? (
          <>
            <Button startIcon={<MenuBook />} variant="secondary" onClick={handleGuideClick} aria-label={guideAriaLabel}>
              {t('cameraGuidance.cameraFooter.actions.guide')}
            </Button>
            <CaptureButton onClick={handleCaptureClick} disabled={captureDisabled} ariaLabel={captureAriaLabel} />
            <Tooltip title={completionTooltipMessage}>
              <div>
                <Button
                  variant={completeDisabled ? 'secondary' : 'primary'}
                  disabled={completeDisabled}
                  onClick={handleCompleteClick}
                  aria-label={completeAriaLabel}
                >
                  {t('cameraGuidance.cameraFooter.actions.complete')}
                </Button>
              </div>
            </Tooltip>
          </>
        ) : (
          <>
            <IconButton onClick={handleGuideClick} color="secondary" aria-label={guideAriaLabel}>
              <MenuBook />
            </IconButton>
            <CaptureButton onClick={handleCaptureClick} disabled={captureDisabled} ariaLabel={captureAriaLabel} />
            <Tooltip title={completionTooltipMessage}>
              <div>
                <IconButton disabled={completeDisabled} onClick={handleCompleteClick} aria-label={completeAriaLabel}>
                  <CheckCircleOutlined color={completeDisabled ? 'disabled' : 'primary'} />
                </IconButton>
              </div>
            </Tooltip>
          </>
        )}
      </FooterControls>
    </Root>
  );
};
